/*
 * Everything Schēma does happens in Calgary, so dates are always rendered in
 * America/Edmonton rather than in whatever timezone the reader's laptop is set
 * to. Otherwise a 7:00pm circle shows as 6:00pm to someone in Vancouver.
 */

const ZONE = "America/Edmonton";

const part = (date, opts) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: ZONE, ...opts }).format(date);

const toDate = (iso) => {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* "Wednesday 14 October" */
export const fmtDayLong = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  return part(d, { weekday: "long", day: "numeric", month: "long" });
};

/* "Wed 14 Oct 2026" */
export const fmtDayShort = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  return part(d, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/* "7:00pm" */
export const fmtTime = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  return part(d, { hour: "numeric", minute: "2-digit", hour12: true })
    .replace(/\s?am/i, "am")
    .replace(/\s?pm/i, "pm");
};

/* "7:00–9:00pm" — drops the meridiem from the start when it's the same */
export const fmtTimeRange = (startIso, endIso) => {
  const a = fmtTime(startIso);
  if (!endIso) return a;
  const b = fmtTime(endIso);
  if (!b) return a;
  const mer = (s) => s.slice(-2);
  if (mer(a) === mer(b)) return `${a.slice(0, -2)}–${b}`;
  return `${a}–${b}`;
};

/* "Wed 14 Oct 2026 · 7:00–9:00pm" */
export const fmtWhen = (startIso, endIso) => {
  const d = fmtDayShort(startIso);
  if (!d) return "";
  return `${d} · ${fmtTimeRange(startIso, endIso)}`;
};

/* For <input type="datetime-local">, which is timezone-naive: give it the
   Calgary wall-clock value so the admin types what people will read. */
export const toLocalInput = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t)?.value || "";
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
};

/* Offset of America/Edmonton at a given instant, e.g. "-06:00" */
const zoneOffset = (utcGuess) => {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: ZONE,
    timeZoneName: "longOffset",
  }).formatToParts(utcGuess);
  const name = p.find((x) => x.type === "timeZoneName")?.value || "GMT-07:00";
  const m = name.match(/GMT([+-]\d{2}):?(\d{2})?/);
  if (!m) return "-07:00";
  return `${m[1]}:${m[2] || "00"}`;
};

/* Turn a datetime-local string (Calgary wall clock) back into a UTC ISO string,
   handling MST/MDT correctly either side of the changeover. */
export const fromLocalInput = (value) => {
  if (!value) return null;
  // First pass with a provisional offset, then re-derive using that instant.
  const first = new Date(`${value}:00${zoneOffset(new Date(`${value}:00Z`))}`);
  const offset = zoneOffset(first);
  return new Date(`${value}:00${offset}`).toISOString();
};

/* "in 4 weeks" / "tomorrow" — quiet, human, no library */
export const relative = (iso) => {
  const d = toDate(iso);
  if (!d) return "";
  const days = Math.round((d.getTime() - Date.now()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 1 && days < 14) return `in ${days} days`;
  if (days >= 14) return `in ${Math.round(days / 7)} weeks`;
  if (days < -1 && days > -14) return `${Math.abs(days)} days ago`;
  return `${Math.round(Math.abs(days) / 7)} weeks ago`;
};

export const KIND_LABEL = {
  circle: "Circle",
  showing: "Showing",
  special: "Special gathering",
  "one-off": "One-off",
  neighbourhoods: "Neighbourhoods",
};

export const kindLabel = (k) => KIND_LABEL[k] || k || "Gathering";
